import { Injectable, inject } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { FirebaseAwareService } from '../shared/firestore/firebase-aware.service';
import type { UserRole } from '../models/user.model';

/** Basic, non-sensitive view of a user for the admin management screen. */
export interface AdminUserListItem {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: UserRole | null;
  emailVerified: boolean;
  disabled: boolean;
  createdAt: number | null;
  lastSignInAt: number | null;
  commissionRate: number | null;
}

@Injectable({ providedIn: 'root' })
export class AdminUserService extends FirebaseAwareService {
  private readonly functions = inject(Functions);

  /** Returns one page of users (Auth) merged with Firestore role/commission. */
  async listUsers(
    pageToken?: string,
  ): Promise<{ users: AdminUserListItem[]; nextPageToken?: string }> {
    return this.inCtx(async () => {
      const call = httpsCallable<
        { pageToken?: string },
        { users: AdminUserListItem[]; nextPageToken?: string }
      >(this.functions, 'listUsersAdmin');
      const res = await call(pageToken ? { pageToken } : {});
      return res.data;
    });
  }

  /**
   * Enables/disables a user account. Disabling also unpublishes the user's
   * active spaces (re-enabling does not republish them).
   */
  async setDisabled(
    uid: string,
    disabled: boolean,
  ): Promise<{ uid: string; disabled: boolean; unpublishedSpaces: number }> {
    return this.inCtx(async () => {
      const call = httpsCallable<
        { uid: string; disabled: boolean },
        { uid: string; disabled: boolean; unpublishedSpaces: number }
      >(this.functions, 'setUserDisabledAdmin');
      const res = await call({ uid, disabled });
      return res.data;
    });
  }

  /** Sets an owner's platform commission rate (decimal, e.g. 0.10 = 10%). */
  async setCommission(uid: string, rate: number): Promise<{ uid: string; rate: number }> {
    return this.inCtx(async () => {
      const call = httpsCallable<
        { uid: string; rate: number },
        { uid: string; rate: number }
      >(this.functions, 'setOwnerCommissionAdmin');
      const res = await call({ uid, rate });
      return res.data;
    });
  }
}
